import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Compass, MapPin } from 'lucide-react';
import { ACCOMMODATIONS, TASTING_STOPS } from '../lib/data';
import { imageUrl } from '../lib/images';
import {
  getRegionsForState,
  getStateHref,
  getStateHubBySlug,
  STATE_HUBS,
} from '../lib/trailHubs';
import { bookingSearchUrl } from '../utils/booking';
import type { Accommodation, TastingStop, USState } from '../types';
import NotFoundPage from './NotFoundPage';

interface ChapterCopy {
  heroNote: string;
  intro: string[];
  featureHeading: string;
  planningHeading: string;
  planningPoints: string[];
}

interface ChapterHeroImage {
  src: string;
  alt: string;
  eyebrow: string;
}

const CHAPTER_COPY: Record<USState, ChapterCopy> = {
  TX: {
    heroNote: 'Texas is the biggest, easiest olive weekend in the project: orchard tastings, river-town browsing, and enough good overnights to make the driving feel light.',
    intro: [
      'Texas gives you the fullest version of the olive trip. Dripping Springs is the polished Hill Country weekend, Gruene adds an easy river-town stop, and the outer ranch detours are there for travelers who want to spend more time with growers and less time in tasting rooms.',
      'That range is what makes Texas such an easy place to begin. You can keep it comfortable and resort-led, or stretch it into a longer backroad loop without losing the feeling that the stops still belong together.',
    ],
    featureHeading: 'Three Texas weekends hiding inside one map.',
    planningHeading: 'How to spend the Texas weekend well.',
    planningPoints: [
      'Base in Dripping Springs if this is your first olive weekend in the Southwest.',
      'Use Gruene as the softer second-night chapter when you want shopping, river walks, and a lower-effort tasting stop.',
      'Treat Georgetown and Elgin as producer detours, not as substitutes for the main Hill Country corridor.',
    ],
  },
  NM: {
    heroNote: 'New Mexico is the most atmospheric chapter in the guide: adobe facades, long golden afternoons, pantry shopping, and tasting rooms that fit naturally into a food-first weekend.',
    intro: [
      'This is where the olive trip turns inward and slows down. Instead of orchards and destination mills, New Mexico offers tasting rooms folded into the kind of weekend that already includes old plazas, chile-heavy dinners, bookstore stops, and an extra hour spent wandering because the light is too good to hurry through.',
      'Santa Fe gives the chapter its beauty and ceremony: walkable streets, hotel courtyards, and a tasting stop that feels like part of a larger food-and-art day. Corrales and Albuquerque make the same chapter feel easier and more lived-in, with simpler logistics, lighter prices, and a pace that works well if you are heading west or just want less friction.',
    ],
    featureHeading: 'Two New Mexico moods, each good for a different kind of traveler.',
    planningHeading: 'How to spend the New Mexico weekend well.',
    planningPoints: [
      'Choose Santa Fe if you want the full high-desert weekend: old streets, strong hotel atmosphere, and one tasting stop folded into a richer day out.',
      'Choose Albuquerque or Corrales if you want an easier overnight, simpler parking, and a tasting stop that works without turning the whole weekend into a splurge.',
      'Keep the chapter compact. New Mexico feels better when it leaves room for museums, shopping, and dinner instead of trying to imitate a larger driving loop.',
    ],
  },
  AZ: {
    heroNote: 'Arizona is the desert chapter: one memorable mill stop, one easy city tasting-room add-on, and a smoother overnight scene than most first-time visitors expect.',
    intro: [
      'The Arizona version of an olive weekend starts in Queen Creek, where the mill gives the trip its real sense of place. You come for the classes, the grove, and the feeling that the stop is substantial enough to plan around rather than squeeze in between other errands.',
      'From there, the state gets easier rather than harder. Phoenix and Scottsdale give you better hotels, dinner options, and one lighter tasting-room stop if you want a second olive note without spending the whole weekend driving across the Valley.',
    ],
    featureHeading: 'A desert weekend built around one strong anchor.',
    planningHeading: 'How to spend the Arizona weekend well.',
    planningPoints: [
      'Build the day around Queen Creek first, then use Phoenix or Scottsdale for the overnight.',
      'Treat the metro tasting room as a complement to the mill, not the reason for the trip.',
      'Expect more driving than Santa Fe, but a cleaner finish than trying to string scattered desert producers into a loop.',
    ],
  },
};

const CHAPTER_HERO_IMAGES: Record<USState, ChapterHeroImage> = {
  TX: {
    src: imageUrl('olive-orchard-field', { w: 1600 }),
    alt: 'Olive orchard landscape under a bright Texas-style sky',
    eyebrow: 'Orchard country',
  },
  NM: {
    src: imageUrl('olive-tasting-plate', { w: 1600 }),
    alt: 'Olive oil tasting plate with bread and small dishes',
    eyebrow: 'Tasting-room mood',
  },
  AZ: {
    src: imageUrl('olive-hills', { w: 1600 }),
    alt: 'Rolling dry hills planted with olive trees',
    eyebrow: 'Desert olive country',
  },
};

function regionLink(state: USState, region: string, path: '/stops' | '/stays') {
  return `${path}?state=${encodeURIComponent(state)}&region=${encodeURIComponent(region)}`;
}

function signatureStop(stop: TastingStop) {
  return (
    <article className="bg-white/80 border border-border rounded-[28px] p-6 space-y-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">Signature stop</p>
      <div className="space-y-2">
        <h3 className="text-3xl font-serif text-ink tracking-tight">{stop.name}</h3>
        <p className="text-sm text-ink-light">{stop.city}, {stop.state}</p>
      </div>
      <p className="text-sm text-ink-light leading-relaxed">{stop.story}</p>
      <div className="flex flex-wrap gap-2">
        {stop.highlights?.map((item) => (
          <span key={item} className="pill-minimal">{item}</span>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 pt-2">
        <Link to={regionLink(stop.state, stop.region, '/stops')} className="btn-minimal">
          Explore this region
        </Link>
        <a href={stop.website} target="_blank" rel="noreferrer noopener" className="btn-minimal">
          Official site
        </a>
      </div>
    </article>
  );
}

function signatureStay(stay: Accommodation) {
  return (
    <article className="bg-sidebar/60 border border-border rounded-[28px] p-6 space-y-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Best stay base</p>
      <div className="space-y-2">
        <h3 className="text-3xl font-serif text-ink tracking-tight">{stay.name}</h3>
        <p className="text-sm text-ink-light">{stay.city}, {stay.state} · {stay.style}</p>
      </div>
      <p className="text-sm text-ink-light leading-relaxed">{stay.description}</p>
      <div className="flex flex-wrap gap-2">
        {stay.amenities.slice(0, 4).map((item) => (
          <span key={item} className="pill-minimal">{item}</span>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 pt-2">
        <Link to={regionLink(stay.state, stay.region, '/stays')} className="btn-minimal">
          See nearby stays
        </Link>
        <a
          href={bookingSearchUrl(stay)}
          target="_blank"
          rel="noreferrer noopener sponsored"
          data-stay22="book"
          className="btn-minimal bg-accent text-white border-accent hover:bg-ink hover:border-ink"
        >
          Find best rates
        </a>
      </div>
    </article>
  );
}

function TexasSection({
  regions,
  stops,
  stays,
}: {
  regions: ReturnType<typeof getRegionsForState>;
  stops: TastingStop[];
  stays: Accommodation[];
}) {
  const polished = regions.find((region) => region.key === 'dripping-springs-fitzhugh');
  const river = regions.find((region) => region.key === 'new-braunfels-gruene');
  const detour = regions.find((region) => region.key === 'central-texas-detours');

  return (
    <>
      <section className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-8">
        <div className="space-y-5">
          <span className="pill-minimal active">Weekend shapes</span>
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight text-ink">
            Three Texas moods, <span className="italic font-light text-accent">not one flat map.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[polished, river, detour].filter(Boolean).map((region) => (
              <article key={region?.key} className="bg-white/80 border border-border rounded-[24px] p-5 space-y-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{region?.shortTitle}</p>
                <p className="text-sm text-ink-light leading-relaxed">{region?.subtitle}</p>
                <p className="text-sm italic text-ink-light">{region?.mood}</p>
              </article>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          {signatureStop(stops[0])}
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-8 items-start">
        {signatureStay(stays[0])}
        <div className="bg-white/80 border border-border rounded-[32px] p-8 space-y-5">
          <span className="pill-minimal active">Weekend flow</span>
          <h2 className="text-4xl font-serif tracking-tight text-ink">Where Texas earns the overnight.</h2>
          <p className="text-lg text-ink-light leading-relaxed">
            Texas is the chapter with the most room to improvise. You can do it as a polished Hill Country getaway with one or two easy tastings, or turn it into a longer backroad weekend with orchard detours and quieter stays.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            <div className="rounded-[24px] bg-sidebar/60 border border-border p-5 space-y-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-ink-light">Start here</p>
              <p className="font-serif text-2xl text-ink">Dripping Springs</p>
            </div>
            <div className="rounded-[24px] bg-sidebar/60 border border-border p-5 space-y-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-ink-light">Second chapter</p>
              <p className="font-serif text-2xl text-ink">Gruene</p>
            </div>
            <div className="rounded-[24px] bg-sidebar/60 border border-border p-5 space-y-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-ink-light">Detour layer</p>
              <p className="font-serif text-2xl text-ink">Georgetown + Elgin</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function NewMexicoSection({
  regions,
  stops,
  stays,
}: {
  regions: ReturnType<typeof getRegionsForState>;
  stops: TastingStop[];
  stays: Accommodation[];
}) {
  const santaFe = regions.find((region) => region.key === 'santa-fe-plaza');
  const albuquerque = regions.find((region) => region.key === 'albuquerque-corrales');

  return (
    <>
      <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
        <div className="bg-white/80 border border-border rounded-[32px] p-8 space-y-6">
          <span className="pill-minimal active">High-desert weekend</span>
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight text-ink">
            Santa Fe and Corrales, <span className="italic font-light text-accent">not as rivals but as different tempos.</span>
          </h2>
          <p className="text-lg text-ink-light leading-relaxed">
            New Mexico works best when you stop expecting it to behave like Texas. This chapter is lighter on long drives and heavier on wandering: late breakfasts, tasting counters, chile shops, old sidewalks, and the kind of hotel bars where it still feels right to sit down before dinner instead of rushing to the next stop.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <article className="rounded-[24px] bg-[linear-gradient(180deg,_rgba(204,120,82,0.12),_rgba(255,255,255,0.8))] border border-border p-5 space-y-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{santaFe?.shortTitle}</p>
              <h3 className="text-2xl font-serif text-ink tracking-tight">Plaza weekend</h3>
              <p className="text-sm text-ink-light leading-relaxed">{santaFe?.subtitle}</p>
              <p className="text-sm italic text-ink-light">{santaFe?.mood}</p>
            </article>
            <article className="rounded-[24px] bg-[linear-gradient(180deg,_rgba(180,156,110,0.12),_rgba(255,255,255,0.8))] border border-border p-5 space-y-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{albuquerque?.shortTitle}</p>
              <h3 className="text-2xl font-serif text-ink tracking-tight">Practical overnight</h3>
              <p className="text-sm text-ink-light leading-relaxed">{albuquerque?.subtitle}</p>
              <p className="text-sm italic text-ink-light">{albuquerque?.mood}</p>
            </article>
          </div>
        </div>
        {signatureStop(stops[0])}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-8 items-start">
        <div className="space-y-4">
          {signatureStay(stays[0])}
          <article className="bg-sidebar/60 border border-border rounded-[28px] p-6 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Best for</p>
            <div className="flex flex-wrap gap-2">
              <span className="pill-minimal">Couples weekends</span>
              <span className="pill-minimal">Walkable afternoons</span>
              <span className="pill-minimal">Food-first trips</span>
              <span className="pill-minimal">One-night add-ons</span>
            </div>
          </article>
        </div>
        <div className="bg-white/80 border border-border rounded-[32px] p-8 space-y-5">
          <span className="pill-minimal active">How it feels</span>
          <h2 className="text-4xl font-serif tracking-tight text-ink">The New Mexico chapter should feel unhurried.</h2>
          <div className="space-y-4 text-lg text-ink-light leading-relaxed">
            <p>Santa Fe is the chapter to choose when you want beauty, old architecture, and a tasting stop that belongs inside a bigger day of galleries, shopping, and dinner reservations.</p>
            <p>Corrales and Albuquerque are better when you want convenience. They let you keep the olive stop in the trip without asking you to organize the whole weekend around the Plaza or pay Santa Fe prices twice.</p>
            <p>The best version of this state leaves room in the schedule. You should finish the day feeling like you saw a city and tasted your way through it, not like you raced through a checklist.</p>
          </div>
        </div>
      </section>
    </>
  );
}

function ArizonaSection({
  regions,
  stops,
  stays,
}: {
  regions: ReturnType<typeof getRegionsForState>;
  stops: TastingStop[];
  stays: Accommodation[];
}) {
  const queenCreek = regions[0];

  return (
    <>
      <section className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
        <div className="bg-white/80 border border-border rounded-[32px] p-8 space-y-5">
          <span className="pill-minimal active">Desert chapter</span>
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight text-ink">
            Start with Queen Creek, <span className="italic font-light text-accent">then let the desert open up.</span>
          </h2>
          <p className="text-lg text-ink-light leading-relaxed">
            Queen Creek gives the chapter its purpose. Once you have that on the calendar, Phoenix and Scottsdale turn the trip into an easy overnight rather than a long out-and-back drive.
          </p>
          <article className="rounded-[24px] bg-sidebar/60 border border-border p-5 space-y-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{queenCreek.shortTitle}</p>
            <p className="text-sm text-ink-light leading-relaxed">{queenCreek.subtitle}</p>
            <p className="text-sm italic text-ink-light">{queenCreek.mood}</p>
          </article>
        </div>
        {signatureStop(stops[0])}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-8 items-start">
        {signatureStay(stays[0])}
        <div className="bg-white/80 border border-border rounded-[32px] p-8 space-y-5">
          <span className="pill-minimal active">Desert overnight</span>
          <h2 className="text-4xl font-serif tracking-tight text-ink">Use the metro for comfort, not for sprawl.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-[24px] bg-sidebar/60 border border-border p-5 space-y-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-ink-light">Anchor stop</p>
              <p className="font-serif text-2xl text-ink">Queen Creek</p>
            </div>
            <div className="rounded-[24px] bg-sidebar/60 border border-border p-5 space-y-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-ink-light">Easy add-on</p>
              <p className="font-serif text-2xl text-ink">North Phoenix</p>
            </div>
            <div className="rounded-[24px] bg-sidebar/60 border border-border p-5 space-y-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-ink-light">Best night base</p>
              <p className="font-serif text-2xl text-ink">Scottsdale</p>
            </div>
          </div>
          <p className="text-lg text-ink-light leading-relaxed">
            Arizona feels best when you treat it as a relaxed desert weekend: one substantial mill stop, one optional city tasting-room browse, and a comfortable overnight that keeps the whole trip from feeling rushed.
          </p>
        </div>
      </section>
    </>
  );
}

export default function StateHubPage({ slug: slugProp }: { slug?: string }) {
  const params = useParams<{ slug: string }>();
  const slug = slugProp ?? params.slug;
  const hub = slug ? getStateHubBySlug(slug) : undefined;

  if (!hub) {
    return <NotFoundPage />;
  }

  const copy = CHAPTER_COPY[hub.state];
  const heroImage = CHAPTER_HERO_IMAGES[hub.state];
  const regions = getRegionsForState(hub.state);
  const stops = TASTING_STOPS.filter((stop) => stop.state === hub.state);
  const stays = ACCOMMODATIONS.filter((stay) => stay.state === hub.state);

  useEffect(() => {
    document.title = `${hub.title} | Olive Oil Trails`;
  }, [hub.title]);

  return (
    <div className="min-h-screen bg-bg">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center text-white">
                <Compass size={16} />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-serif text-lg text-ink">Olive Oil Trails</span>
                <span className="text-[10px] uppercase tracking-widest text-ink-light">
                  Southwest tasting weekends
                </span>
              </div>
            </Link>
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-light hover:text-ink transition-colors"
            >
              <ArrowLeft size={14} /> Back to the route
            </Link>
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-ink-light">State chapters</div>
            <nav className="flex flex-wrap gap-2">
              {STATE_HUBS.map((candidate) => (
                <Link
                  key={candidate.state}
                  to={getStateHref(candidate.state)}
                  className={candidate.state === hub.state ? 'pill-minimal active' : 'pill-minimal'}
                >
                  {candidate.state === 'TX' ? 'Texas' : candidate.state === 'NM' ? 'New Mexico' : 'Arizona'}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20 space-y-16">
        <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
          <div className="space-y-6">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-border rounded-full text-[10px] font-bold uppercase tracking-widest text-ink">
              <MapPin size={12} className="text-accent" />
              {hub.eyebrow}
            </span>
            <h1 className="text-5xl md:text-7xl font-serif text-ink tracking-tight leading-[1.02]">
              {hub.title}
            </h1>
            <p className="text-lg md:text-xl text-ink-light leading-relaxed max-w-4xl">
              {hub.subtitle}
            </p>
            <p className="text-lg text-ink-light leading-relaxed max-w-4xl">
              {copy.heroNote}
            </p>
            <div className="flex flex-wrap gap-2">
              {hub.bestFor.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1 bg-sidebar text-ink text-[10px] font-bold uppercase tracking-widest rounded-full"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <article className="relative overflow-hidden rounded-[32px] border border-border min-h-[360px] bg-sidebar/60">
            <img
              src={heroImage.src}
              alt={heroImage.alt}
              className="absolute inset-0 h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(14,18,12,0.08),rgba(14,18,12,0.58))]" />
            <div className="relative h-full flex flex-col justify-end p-8 text-white">
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/80">{heroImage.eyebrow}</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-serif tracking-tight max-w-md">
                Olive weekends look different in every state.
              </h2>
            </div>
          </article>
        </section>

        <section className="rounded-[28px] border border-border bg-[linear-gradient(135deg,rgba(255,255,255,0.92),rgba(245,241,232,0.88))] p-6 md:p-7">
          <div className="max-w-4xl space-y-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Before you go</p>
            <p className="text-sm md:text-base text-ink-light leading-relaxed">
              Hours, tasting prices, class schedules, seasonal access, and reservation policies can change without much notice.
              Always double-check the official site or call ahead before you drive out, especially for rural stops, holiday weekends, and same-day visits.
            </p>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card-minimal p-6 bg-white/80">
            <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Live tasting stops</p>
            <p className="text-4xl font-serif text-accent mt-2">{stops.length}</p>
          </div>
          <div className="card-minimal p-6 bg-white/80">
            <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Stay bases</p>
            <p className="text-4xl font-serif text-accent mt-2">{stays.length}</p>
          </div>
          <div className="card-minimal p-6 bg-white/80">
            <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Driveable regions</p>
            <p className="text-4xl font-serif text-accent mt-2">{regions.length}</p>
          </div>
        </section>

        <section className="max-w-4xl space-y-4">
          <span className="pill-minimal active">At a glance</span>
          <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
            {copy.featureHeading}
          </h2>
          {copy.intro.map((paragraph) => (
            <p key={paragraph} className="text-lg text-ink-light leading-relaxed">
              {paragraph}
            </p>
          ))}
        </section>

        {hub.state === 'TX' ? <TexasSection regions={regions} stops={stops} stays={stays} /> : null}
        {hub.state === 'NM' ? <NewMexicoSection regions={regions} stops={stops} stays={stays} /> : null}
        {hub.state === 'AZ' ? <ArizonaSection regions={regions} stops={stops} stays={stays} /> : null}

        <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-8 items-start">
          <div className="bg-white/80 border border-border rounded-[32px] p-8 space-y-5">
            <span className="pill-minimal active">Planning notes</span>
            <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight">
              {copy.planningHeading}
            </h2>
            <div className="grid gap-4">
              {copy.planningPoints.map((point) => (
                <div key={point} className="rounded-[24px] bg-sidebar/60 border border-border p-5 text-ink-light leading-relaxed">
                  {point}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/80 border border-border rounded-[32px] p-8 space-y-5">
            <span className="pill-minimal active">Other state chapters</span>
            <div className="grid gap-4">
              {STATE_HUBS.filter((candidate) => candidate.state !== hub.state).map((candidate) => (
                <Link
                  key={candidate.state}
                  to={getStateHref(candidate.state)}
                  className="rounded-[24px] bg-sidebar/60 border border-border p-5 space-y-2 hover:border-accent hover:bg-white transition-colors"
                >
                  <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{candidate.eyebrow}</p>
                  <h3 className="text-2xl font-serif text-ink tracking-tight">{candidate.title}</h3>
                  <p className="text-sm text-ink-light leading-relaxed">{candidate.subtitle}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
